/**
 * One product, as a comparable card.
 *
 * SHARED BY THE BILLING PAGE AND ONBOARDING, which is the whole reason it exists as a
 * component: those two screens present the same seven products, and a second copy of
 * the card is where the price, the feature list or the "Owned" state start disagreeing
 * between the screen that sells a plan and the screen that confirms you have it.
 *
 * EVERY VALUE COMES FROM THE PRODUCT PASSED IN. Nothing here invents, rounds, discounts
 * or reorders anything — the name, tag, price, description and features are read off
 * `data/products.ts` exactly as they are defined there, and ownership is supplied from
 * the authenticated session's webhook-confirmed checkout projection. See the note on
 * RECOMMENDED_PRODUCT_ID for the one presentational flag in the data.
 *
 * WHAT MAKES THE STATES OBVIOUS, since "make the current plan obvious" is the point:
 *   - owned   → accent border, a filled "Owned" pill where the button was, and the
 *               price greyed, because it is no longer an amount you would pay
 *   - highly recommended → a FILLED accent ribbon reading "Highly Recommended" and an
 *               accent border, one step above the outlined ribbon below
 *   - recommended → an outlined ribbon above the name and a stronger border, so it is
 *               findable without reading all seven cards
 *   - supporting badge → an extra outlined badge beside the main ribbon, for a second
 *               short note such as "Advanced Entry" without inventing a new card style
 *   - primary highlighted → accent border and a soft glow, for a featured card that
 *               still sits on the normal surface
 *   - secondary highlighted → the soft-accent surface with the accent icon, for a
 *               featured card that should read as gentler than the primary one
 *   - locked  → a padlocked, disabled action and a stated reason; see `locked`
 *   - neither → plain border, one clear primary action
 * Visibly different cards, none of which rely on colour alone: the pill carries a tick
 * and the word "Owned", and each ribbon carries its own words.
 *
 * `compact` drops the feature list to the three most distinguishing lines, for
 * onboarding — where the job is choosing between plans quickly, not auditing one. The
 * billing page shows all of them.
 */
import { Check, CheckCircle2, Lock, X } from 'lucide-react';
import { Btn, Tooltip } from '../ui';
import type { UpgradeProduct } from '../../data/upgradeOffers';
import { c } from '../../styles/theme';

/**
 * How many features a compact card lists.
 *
 * Three, because that is what fits beside a price and a button on a 380px phone
 * without the card becoming a wall of text — the failure mode §1 names as
 * "overcrowded". The full list is one tap away on the billing page.
 */
const COMPACT_FEATURES = 3;

export const ADDON_BUY_DISABLED_REASON = 'Product not available for purchase this way';

/**
 * The shape this card renders.
 *
 * Shared with the catalogue data so every offer renders through one card shape.
 */
export type PlanCardPlan = UpgradeProduct;
export interface PlanCardProps {
  product: PlanCardPlan;
  /** True only when server session data confirms this checkout or an including bundle. */
  owned: boolean;
  /** Draws the outlined recommendation ribbon. From data.RECOMMENDED_PRODUCT_ID. */
  recommended?: boolean;
  /** Wording on the outlined recommendation ribbon. Defaults to "Recommended". */
  recommendedLabel?: string;
  /** Extra outlined badge shown beside the main ribbon, using the recommendation styling. */
  supportingBadgeLabel?: string;
  /** Accents the card itself while keeping the outlined recommendation ribbon style. */
  highlightVariant?: 'primary' | 'secondary';
  /** Alternate card structure for narrow, add-on style rows. */
  layout?: 'default' | 'addon-inline';
  /**
   * Draws the filled "Highly Recommended" ribbon. From data.HIGHLY_RECOMMENDED_PRODUCT_ID.
   *
   * A stronger mark than `recommended`, and distinct from it in wording as well as in
   * treatment, so the two can sit on two cards in one grid without either being ambiguous.
   * Presentation only — nothing gates on it.
   */
  highlyRecommended?: boolean;
  /** Buys it. Not called when `owned` or `locked`, because no live button is rendered. */
  onBuy: () => void;
  /** Checkout destination exposed directly on the rendered buy control. */
  buyHref?: string;
  checkoutImageUrl?: string;
  /** Wording on the action, e.g. "Buy" on billing and "Choose plan" in onboarding. */
  buyLabel?: string;
  /** Disables the action while a purchase is in flight. */
  busy?: boolean;
  /** Disables an otherwise buyable action and explains the temporary restriction. */
  buyDisabledReason?: string;
  /** Shortens the feature list — see COMPACT_FEATURES. */
  compact?: boolean;
  /**
   * Renders the plan as announced-but-unbuyable: a padlocked, genuinely disabled action.
   *
   * Present-and-disabled rather than absent, which is the convention the Studio's upload
   * panel already follows: hiding the control would hide the plan, and a live button that
   * toasted "coming soon" would be a purchase flow that silently does nothing. The reason
   * belongs beside it — see `lockedDescribedBy`.
   */
  locked?: boolean;
  /** Wording on the disabled action. */
  lockedLabel?: string;
  /**
   * Id of the element explaining why it is locked.
   *
   * Needed because a disabled button fires no pointer events, so a tooltip on one never
   * opens — the explanation has to be associated with the element itself. Same reason
   * Btn carries `aria-describedby`.
   */
  lockedDescribedBy?: string;
  /**
   * What the plan explicitly does NOT grant, by product name.
   *
   * Rendered as its own crossed-out list rather than as a sentence in the description: a
   * bundle read only by what it includes is the one thing a buyer misreads, and the two
   * exclusions here are the most expensive products in the catalogue.
   */
  excludes?: string[];
}

export default function PlanCard({
  product,
  owned,
  recommended,
  recommendedLabel = 'Recommended',
  supportingBadgeLabel,
  highlightVariant,
  layout = 'default',
  highlyRecommended,
  onBuy,
  buyHref,
  checkoutImageUrl,
  buyLabel = 'Buy',
  busy,
  buyDisabledReason,
  compact,
  locked,
  lockedLabel = 'Not available yet',
  lockedDescribedBy,
  excludes,
}: PlanCardProps) {
  const Icon = product.icon;
  const features = compact ? product.features.slice(0, COMPACT_FEATURES) : product.features;
  const primaryHighlighted = highlightVariant === 'primary';
  const secondaryHighlighted = highlightVariant === 'secondary';
  const addonInline = layout === 'addon-inline';
  const titleColor = secondaryHighlighted && !owned ? c.accent : c.text1;
  const bodyColor = secondaryHighlighted && !owned ? c.accent : c.text2;
  const priceColor = owned ? c.text2 : secondaryHighlighted ? c.accent : c.text1;
  const priceMetaColor = owned ? c.text2 : secondaryHighlighted ? c.accent : c.text2;
  const tagColor = secondaryHighlighted && !owned ? c.accent : c.text2;
  const tagBorder = secondaryHighlighted && !owned ? c.accentBorder : c.border;
  const tagBackground = secondaryHighlighted && !owned ? c.surface1 : 'transparent';
  const priceMeta = product.priceMeta ?? 'one-time';
  const priceMetaBold = product.priceMetaBold;
  const priceMetaParts =
    priceMetaBold && priceMeta.includes(priceMetaBold) ? priceMeta.split(priceMetaBold) : null;

  return (
    <div
      className="lyx-plan-card"
      style={{
        background: secondaryHighlighted && !owned ? c.accentSoft : c.surface1,
        boxShadow: primaryHighlighted && !owned ? `0 0 0 2px ${c.accentSoft}` : 'none',
        // Owned wins over both recommendations: what you have is more important to see
        // than what we suggest, and recommending something already owned would be noise.
        border: owned
          ? `1.5px solid ${c.accentBorder}`
          : highlyRecommended || primaryHighlighted
            ? `1.5px solid ${c.accent}`
            : secondaryHighlighted
              ? `1.5px solid ${c.accentBorder}`
              : recommended
                ? `1.5px solid ${c.borderStrong}`
                : `1px solid ${c.border}`,
      }}
    >
      {/* Above the name rather than floating over the corner, so it cannot cover the
          product name at a narrow width — the overlap §8 rules out.

          At most one ribbon: "Highly Recommended" outranks "Recommended", and a card
          carrying both would be saying one thing twice. */}
      {highlyRecommended && !owned ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 8,
          }}
        >
          <span
            className="lyx-plan-ribbon"
            /* FILLED, where the plain recommendation is outlined — and with the dark
               on-accent foreground the primary button uses, which contrast.test.ts pins at
               AA against this exact fill. */
            style={{
              background: c.accentFill,
              color: '#1A1300',
              border: `1px solid ${c.accentFill}`,
              marginBottom: 0,
            }}
          >
            Highly Recommended
          </span>
          {supportingBadgeLabel ? (
            <span
              className="lyx-plan-ribbon"
              style={{
                background: c.accentSoft,
                color: c.accent,
                border: `1px solid ${c.accentBorder}`,
                marginBottom: 0,
              }}
            >
              {supportingBadgeLabel}
            </span>
          ) : null}
        </div>
      ) : recommended && !owned ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 8,
          }}
        >
          <span
            className="lyx-plan-ribbon"
            style={{
              background: c.accentSoft,
              color: c.accent,
              border: `1px solid ${c.accentBorder}`,
              marginBottom: 0,
            }}
          >
            {recommendedLabel}
          </span>
          {supportingBadgeLabel ? (
            <span
              className="lyx-plan-ribbon"
              style={{
                background: c.accentSoft,
                color: c.accent,
                border: `1px solid ${c.accentBorder}`,
                marginBottom: 0,
              }}
            >
              {supportingBadgeLabel}
            </span>
          ) : null}
        </div>
      ) : null}

      {addonInline ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div
            className="lyx-plan-head"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              minWidth: 0,
              flex: '1 1 320px',
              marginBottom: 0,
            }}
          >
            <Icon
              size={16}
              color={owned || secondaryHighlighted ? c.accent : c.text2}
              style={{ flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 2,
                }}
              >
                <p style={{ fontWeight: 500, fontSize: 13, margin: 0, color: titleColor }}>
                  {product.name}
                </p>
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: 9,
                    color: tagColor,
                    background: tagBackground,
                    border: `1px solid ${tagBorder}`,
                    borderRadius: 999,
                    padding: '1px 7px',
                  }}
                >
                  {product.tag}
                </span>
              </div>
              <p style={{ fontSize: 11, color: bodyColor, margin: 0, lineHeight: 1.5 }}>
                {product.desc}
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 10,
              flex: '0 1 auto',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'IBM Plex Mono',
                color: priceColor,
              }}
            >
              ${product.priceDisplay ?? product.price}
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 400,
                  color: priceMetaColor,
                  fontFamily: 'Inter',
                }}
              >
                {' '}
                {priceMetaParts ? (
                  <>
                    {priceMetaParts[0]}
                    <strong style={{ color: 'inherit' }}>{priceMetaBold}</strong>
                    {priceMetaParts.slice(1).join(priceMetaBold)}
                  </>
                ) : (
                  priceMeta
                )}
              </span>
            </span>
            {owned ? (
              <Btn disabled onClick={undefined} aria-label={`${product.name} — owned`}>
                <CheckCircle2 size={12} />
                Owned
              </Btn>
            ) : buyDisabledReason ? (
              <Tooltip label={buyDisabledReason} describe>
                <span
                  tabIndex={0}
                  aria-label={`${buyLabel} ${product.name} unavailable`}
                  style={{ display: 'inline-flex' }}
                >
                  <Btn disabled onClick={undefined} aria-label={`${buyLabel} ${product.name}`}>
                    {buyLabel}
                  </Btn>
                </span>
              </Tooltip>
            ) : locked ? (
              <Btn
                disabled
                onClick={undefined}
                aria-label={`${product.name} — ${lockedLabel.toLowerCase()}`}
                aria-describedby={lockedDescribedBy}
              >
                <Lock size={12} />
                {lockedLabel}
              </Btn>
            ) : (
              <Btn
                disabled={busy}
                onClick={onBuy}
                href={buyHref}
                checkoutImageUrl={checkoutImageUrl}
                pendingOnNavigate={Boolean(buyHref)}
                aria-label={`${buyLabel} ${product.name}`}
              >
                {buyLabel}
              </Btn>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="lyx-plan-head">
            <Icon
              size={17}
              color={owned || secondaryHighlighted ? c.accent : c.text2}
              style={{ flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 500, fontSize: 13, margin: 0, color: titleColor }}>
                {product.name}
              </p>
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 3,
                  fontSize: 9,
                  color: tagColor,
                  background: tagBackground,
                  border: `1px solid ${tagBorder}`,
                  borderRadius: 999,
                  padding: '1px 7px',
                }}
              >
                {product.tag}
              </span>
            </div>
          </div>

          {product.trial ? (
            <>
              <p style={{ fontSize: 11, color: bodyColor, margin: '0 0 10px', lineHeight: 1.5 }}>
                {product.desc}
              </p>
              <div
                style={{
                  background: c.accentSoft,
                  border: `1px solid ${c.accentBorder}`,
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <strong
                  style={{ display: 'block', fontSize: 13, color: titleColor, marginBottom: 5 }}
                >
                  {product.trial.headline}
                </strong>
                <p style={{ fontSize: 11, color: bodyColor, margin: 0, lineHeight: 1.5 }}>
                  {product.trial.terms}
                </p>
              </div>
            </>
          ) : null}

          {/* The billing qualifier is spelled out on the card rather than only in a page-level
              note: a card compared in isolation should not leave the reader guessing whether
              $97 is one-time, yearly, or something else. Most cards default to one-time. */}
          <p className="lyx-plan-price" style={{ color: priceColor }}>
            ${product.priceDisplay ?? product.price}
            <span
              style={{ fontSize: 10, fontWeight: 400, color: priceMetaColor, fontFamily: 'Inter' }}
            >
              {' '}
              {priceMetaParts ? (
                <>
                  {priceMetaParts[0]}
                  <strong style={{ color: 'inherit' }}>{priceMetaBold}</strong>
                  {priceMetaParts.slice(1).join(priceMetaBold)}
                </>
              ) : (
                priceMeta
              )}
            </span>
          </p>

          {product.priceSummary ? (
            <p style={{ fontSize: 11, color: bodyColor, margin: '-3px 0 9px', lineHeight: 1.5 }}>
              {product.priceSummary}
            </p>
          ) : null}

          {product.billingNote ? (
            <p style={{ fontSize: 10, color: bodyColor, margin: '-3px 0 9px', lineHeight: 1.4 }}>
              {product.billingNote}
            </p>
          ) : null}

          {!product.trial ? (
            <p style={{ fontSize: 11, color: bodyColor, margin: '0 0 10px', lineHeight: 1.5 }}>
              {product.desc}
            </p>
          ) : null}

          {/* A list, not a row of chips. Chips wrap into an unscannable block at these
              widths, and the point of this page is comparing one card against another —
              which needs the features on their own lines to line up by eye.

              Omitted entirely when there is nothing to list yet, so a placeholder card can
              ship with its name, price and description only instead of rendering an empty
              list block. */}
          {features.length > 0 ? (
            <ul className="lyx-plan-features">
              {features.map((f) => (
                <li key={f}>
                  <Check
                    size={11}
                    color={owned || secondaryHighlighted ? c.accent : c.text2}
                    style={{ flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 11, color: bodyColor, whiteSpace: 'pre-line' }}>
                    {f}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {product.featureNote ? (
            <p style={{ fontSize: 11, color: bodyColor, margin: '2px 0 12px', lineHeight: 1.5 }}>
              {product.featureNote}
            </p>
          ) : null}

          {/* ---- what it does not include ----
              Same list treatment with the tick replaced by a cross, so the two lists are
              comparable by eye and the difference is not carried by colour alone: the heading
              says "Not included" in words. A bundle whose exclusions were a footnote in the
              description is how someone buys it for the one product it leaves out. */}
          {excludes && excludes.length > 0 ? (
            <div style={{ margin: '0 0 12px' }}>
              <p
                style={{
                  fontSize: 9,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: c.text2,
                  fontWeight: 600,
                  margin: '0 0 5px',
                }}
              >
                Not included
              </p>
              <ul className="lyx-plan-features" style={{ margin: 0 }}>
                {excludes.map((f) => (
                  <li key={f}>
                    <X size={11} color={c.text2} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: c.text2, textDecoration: 'line-through' }}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Pinned to the bottom by .lyx-plan-card's flex column, so the actions across a
              row of cards line up however unequal the feature lists are. */}
          <div className="lyx-plan-action">
            {owned ? (
              <Btn full disabled onClick={undefined} aria-label={`${product.name} — owned`}>
                <CheckCircle2 size={12} />
                Owned
              </Btn>
            ) : buyDisabledReason ? (
              <Tooltip label={buyDisabledReason} describe style={{ width: '100%' }}>
                <span
                  tabIndex={0}
                  aria-label={`${buyLabel} ${product.name} unavailable`}
                  style={{ display: 'inline-flex', width: '100%' }}
                >
                  <Btn full disabled onClick={undefined} aria-label={`${buyLabel} ${product.name}`}>
                    {buyLabel}
                  </Btn>
                </span>
              </Tooltip>
            ) : locked ? (
              /* GENUINELY DISABLED, not a button that toasts an apology: `disabled` is the
                 real attribute, so there is no click handler and no code path from this card
                 to an entitlement. `onBuy` is not wired up at all here. */
              <Btn
                full
                disabled
                onClick={undefined}
                aria-label={`${product.name} — ${lockedLabel.toLowerCase()}`}
                aria-describedby={lockedDescribedBy}
              >
                <Lock size={12} />
                {lockedLabel}
              </Btn>
            ) : (
              <Btn
                full
                disabled={busy}
                onClick={onBuy}
                href={buyHref}
                checkoutImageUrl={checkoutImageUrl}
                pendingOnNavigate={Boolean(buyHref)}
                /* Names the product, because a grid of seven cards otherwise presents
                   seven buttons all called "Buy". Pinned by tests. */
                aria-label={`${buyLabel} ${product.name}`}
              >
                {buyLabel}
              </Btn>
            )}
          </div>
        </>
      )}
    </div>
  );
}
